import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  type TableCellProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { CreateBudgetovacPositionModal } from "@src/components/modules/budgets/positions/components";
import { BudgetPositionCreateStore } from "@src/components/modules/budgets/positions/stores/create";
import {
  Form,
  LoadingOverlay,
  NumberInput,
  Select,
  TextField,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";

const BudgetovacPositionsSetForm = observer(
  function BudgetovacPositionsSetForm() {
    const { budgetovacPositionsSetModalStore: store } = useStore();
    const form = store.modalForm.$;
    const { createPositionStore } = useLocalObservable(() => ({
      createPositionStore: new BudgetPositionCreateStore(
        store.onCreatePosition,
      ),
    }));

    return (
      <VStack align="stretch" spacing="4">
        <TextField
          label={t`Rate card name`}
          placeholder={t`Rate card name`}
          value={form.name.value}
          onChange={form.name.onChange}
          error={form.name.error}
        />

        <HStack align="flex-end" spacing="4">
          <Box w="full">
            <Select
              label={t`Brand`}
              isClearable
              options={store.brandOptions}
              value={form.client_id.value}
              onChange={form.client_id.onChange}
              isLoading={store.isLoadingDependencies}
              placeholder={t`Default rate card`}
              asPortal
            />
          </Box>

          {!store.isDuplicateMode && (
            <Box w="full">
              <Select
                label={t`Currency`}
                value={form.currency_code.value}
                onChange={form.currency_code.onChange}
                options={store.currencyOptions}
                asPortal
              />
            </Box>
          )}

          <Box w="full" mb="2.5">
            <Checkbox
              isChecked={form.active.value}
              onChange={(e) => form.active.onChange(e.target.checked)}
            >
              <Trans>Default card</Trans>
            </Checkbox>
          </Box>
        </HStack>

        {!store.isDuplicateMode && (
          <>
            <Divider my="4" />

            <Box>
              <Box mb="4" fontWeight="semibold">
                <Trans>Positions</Trans>
              </Box>

              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>
                      <Trans>Name</Trans>
                    </Th>
                    <Th>
                      <Trans>Price</Trans>
                    </Th>
                    <Th>
                      <Trans>Department</Trans>
                    </Th>
                    <Th w="50px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {form.position_prices.$.map(({ $ }, index) => {
                    const isLast = index === form.position_prices.$.length - 1;
                    const borderStyle: TableCellProps["sx"] = {
                      verticalAlign: "top",
                      borderBottomWidth: isLast ? "0" : undefined,
                    };

                    return (
                      <Tr key={$.position_id.value}>
                        <Td
                          sx={{
                            borderBottomWidth: isLast ? "0" : undefined,
                          }}
                        >
                          {$.name.value}
                        </Td>
                        <Td sx={borderStyle}>
                          <NumberInput
                            value={$.price.value}
                            onChange={$.price.onChange}
                            minValue={0}
                            error={$.price.error}
                          />
                        </Td>
                        <Td sx={borderStyle}>
                          <Select
                            value={$.department_id.value}
                            onChange={(val) => $.department_id.onChange(val)}
                            options={store.departmentOptions}
                            placeholder={t`Select department`}
                            isClearable
                            size="sm"
                            error={$.department_id.error}
                          />
                        </Td>
                        <Td sx={borderStyle}>
                          <IconButton
                            aria-label={t`Remove position`}
                            colorScheme="red"
                            icon={<Icon name="trash-03" />}
                            onClick={() =>
                              store.removePositionRow($.position_id.value)
                            }
                            size="sm"
                            variant="ghost"
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>

              {store.availablePositions.length > 0 && (
                <Box mt="4">
                  <Select
                    placeholder={t`+ Add position`}
                    value=""
                    onChange={(val) => {
                      if (!val) return;
                      store.addPositionRow(val);
                    }}
                    options={store.availablePositions}
                    error={store.modalForm.formError ?? undefined}
                    asPortal
                    topExtraContent={
                      <Button
                        leftIcon={<Icon name="plus" />}
                        onClick={createPositionStore.disclosure.onOpen}
                        variant="link"
                      >
                        <Trans>Add new position</Trans>
                      </Button>
                    }
                  />
                </Box>
              )}

              <CreateBudgetovacPositionModal store={createPositionStore} />
            </Box>
          </>
        )}

        {!store.isDuplicateMode && (
          <>
            <Divider my="4" />

            <Box>
              <Box mb="4" fontWeight="semibold">
                <Trans>Commissions</Trans>
              </Box>

              <Grid gap="4" templateColumns="repeat(4, 1fr)">
                <GridItem>
                  <NumberInput
                    label={t`Internal production (%)`}
                    value={String(
                      form.internal_production_commission.value
                        .denominatedValue,
                    )}
                    onChange={(value) => {
                      store.setDenomValueToField(
                        form.internal_production_commission,
                        value,
                      );
                    }}
                    minValue={0}
                    maxValue={100}
                  />
                </GridItem>
                <GridItem>
                  <NumberInput
                    label={t`External production (%)`}
                    value={String(
                      form.external_production_commission.value
                        .denominatedValue,
                    )}
                    onChange={(value) =>
                      store.setDenomValueToField(
                        form.external_production_commission,
                        value,
                      )
                    }
                    minValue={0}
                    maxValue={100}
                  />
                </GridItem>
                <GridItem>
                  <NumberInput
                    label={t`Media budget (%)`}
                    value={String(
                      form.media_budget_commission.value.denominatedValue,
                    )}
                    onChange={(value) =>
                      store.setDenomValueToField(
                        form.media_budget_commission,
                        value,
                      )
                    }
                    minValue={0}
                    maxValue={100}
                  />
                </GridItem>
                <GridItem>
                  <NumberInput
                    label={t`Royalties (%)`}
                    value={String(
                      form.extras_commission.value.denominatedValue,
                    )}
                    onChange={(value) =>
                      store.setDenomValueToField(form.extras_commission, value)
                    }
                    minValue={0}
                    maxValue={100}
                  />
                </GridItem>
              </Grid>
            </Box>
          </>
        )}
      </VStack>
    );
  },
);

export const BudgetovacPositionsSetModal = observer(
  function BudgetovacPositionsSetModal() {
    const { budgetovacPositionsSetModalStore: store } = useStore();

    return (
      <Modal
        closeOnOverlayClick={!store.isSubmitting}
        isCentered
        isOpen={store.modalState.isOpen}
        onClose={store.modalState.onClose}
        scrollBehavior="inside"
        size="5xl"
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{store.modalTitle}</ModalHeader>
          <ModalCloseButton isDisabled={store.isSubmitting} />

          <ModalBody>
            <LoadingOverlay
              isLoading={store.isLoadingDetail || store.isLoadingDependencies}
              size="md"
              position="absolute"
              transparent={false}
            >
              <Form onSubmit={store.onSubmit} id="budgetovacPositionsSetForm">
                <BudgetovacPositionsSetForm />
              </Form>
            </LoadingOverlay>
          </ModalBody>

          <ModalFooter>
            <Button
              isLoading={store.isSubmitting}
              onClick={store.modalState.onClose}
              type="reset"
              variant="ghost"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              ml="2"
              form="budgetovacPositionsSetForm"
              isLoading={store.isSubmitting}
              type="submit"
            >
              {store.isDuplicateMode ? (
                <Trans>Duplicate</Trans>
              ) : store.isEditing ? (
                <Trans>Update</Trans>
              ) : (
                <Trans>Create</Trans>
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);
